import {
  collection,
  doc,
  addDoc,
  getFirestore,
  getDocs,
  getDoc,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  DocumentData,
  QueryDocumentSnapshot,
  Timestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app } from "../../lib/firebase"; // Changed manually
const db = getFirestore(app); // Changed manually

interface UserProfile {
  userId: string;
  email: string;
  photoURL: string;
  createdAt: Date;
  updatedAt: Date;
  displayName: string;
  friendRequests?: string[];
  friends?: string[];
}

interface Post {
  id: string;
  content: string;
  userId: string;
  likedBy: string[];
  commentIds: string[];
  createdAt: Date;
  imageUrl?: string;
  videoUrl?: string;
}

interface Comment {
  id: string;
  text: string;
  userId: string;
  username: string;
  postId: string;
  createdAt: Date;
}

interface FirestorePost extends Omit<Post, "createdAt"> {
  createdAt: Timestamp;
}

interface FirestoreComment extends Omit<Comment, "createdAt"> {
  createdAt: Timestamp;
}

// Function to create a new post
const createPost = async (
  content: string,
  userId: string,
  username: string,
  imageUrl?: string,
  videoUrl?: string
) => {
  try {
    console.log("THE USER: ", username);
    const postsCollection = collection(db, "posts");
    await addDoc(postsCollection, {
      content,
      userId,
      username,
      likedBy: [],
      imageUrl: imageUrl || null,
      videoUrl: videoUrl || null,
      createdAt: serverTimestamp(),
    });
    console.log("Post created successfully!");
  } catch (error) {
    console.error("Error creating post:", error);
    throw error;
  }
};

// Function to get recent posts with pagination
const getPosts = async (
  limitCount: number = 10,
  lastPost?: QueryDocumentSnapshot<DocumentData>
): Promise<Post[]> => {
  try {
    let postsQuery;
    if (lastPost) {
      postsQuery = query(
        collection(db, "posts"),
        orderBy("createdAt", "desc"),
        limit(limitCount),
        where("createdAt", "<", lastPost.data().createdAt)
      );
    } else {
      postsQuery = query(
        collection(db, "posts"),
        orderBy("createdAt", "desc"),
        limit(limitCount)
      );
    }

    const querySnapshot = await getDocs(postsQuery);
    const posts: Post[] = [];

    // Get all posts first
    const postDocs = querySnapshot.docs.map((doc) => {
      const data = doc.data() as FirestorePost;
      return {
        ...data,
        id: doc.id,
        commentIds: [], // Initialize empty array for comment IDs
      };
    });

    // Get comments for all posts in a single query
    const commentsQuery = query(
      collection(db, "comments"),
      where(
        "postId",
        "in",
        postDocs.map((post) => post.id)
      ),
      orderBy("createdAt", "desc")
    );

    const commentsSnapshot = await getDocs(commentsQuery);
    const commentIdsByPost = new Map<string, string[]>();

    commentsSnapshot.forEach((doc) => {
      const data = doc.data() as FirestoreComment;
      if (!commentIdsByPost.has(data.postId)) {
        commentIdsByPost.set(data.postId, []);
      }
      commentIdsByPost.get(data.postId)?.push(doc.id);
    });

    // Combine posts with their comment IDs
    postDocs.forEach((doc) => {
      posts.push({
        id: doc.id,
        content: doc.content,
        userId: doc.userId,
        likedBy: doc.likedBy,
        imageUrl: doc.imageUrl,
        videoUrl: doc.videoUrl,
        createdAt: doc.createdAt.toDate(),
        commentIds: commentIdsByPost.get(doc.id) || [],
      });
    });

    return posts;
  } catch (error) {
    console.error("Error getting posts:", error);
    throw error;
  }
};

// Function to create a new comment
const createComment = async (
  postId: string,
  userId: string,
  username: string,
  text: string
) => {
  try {
    const commentsCollection = collection(db, "comments");
    const commentDoc = await addDoc(commentsCollection, {
      postId,
      userId,
      username,
      text,
      createdAt: serverTimestamp(),
    });

    const postRef = doc(db, "posts", postId);
    await updateDoc(postRef, {
      commentIds: arrayUnion(commentDoc.id),
    });

    console.log("Comment added successfully!");
    return commentDoc.id;
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

// Function to get comments by their IDs
const getCommentsByIds = async (commentIds: string[]): Promise<Comment[]> => {
  try {
    if (commentIds.length === 0) return [];

    const commentsQuery = query(
      collection(db, "comments"),
      where("__name__", "in", commentIds)
    );

    const querySnapshot = await getDocs(commentsQuery);
    const comments: Comment[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data() as FirestoreComment;
      comments.push({
        id: doc.id,
        text: data.text,
        userId: data.userId,
        username: data.username,
        postId: data.postId,
        createdAt: data.createdAt.toDate(),
      });
    });

    return comments;
  } catch (error) {
    console.error("Error getting comments:", error);
    throw error;
  }
};

// Function to like a post
const likePost = async (postId: string, userId: string) => {
  try {
    const postRef = doc(db, "posts", postId);
    await updateDoc(postRef, {
      likedBy: arrayUnion(userId),
    });
    console.log("Post liked!");
  } catch (error) {
    console.error("Error liking post:", error);
    throw error;
  }
};

// Function to unlike a post
const unlikePost = async (postId: string, userId: string) => {
  try {
    const postRef = doc(db, "posts", postId);
    await updateDoc(postRef, {
      likedBy: arrayRemove(userId),
    });
    console.log("Post unliked!");
  } catch (error) {
    console.error("Error unliking post:", error);
    throw error;
  }
};

// Function to send a friend request
const sendFriendRequest = async (senderId: string, receiverId: string) => {
  try {
    const receiverRef = doc(db, "userProfiles", receiverId);
    const senderRef = doc(db, "userProfiles", senderId);

    await updateDoc(receiverRef, {
      friendRequests: arrayUnion(senderId),
      updatedAt: serverTimestamp(),
    });

    await updateDoc(senderRef, {
      friendRequests: arrayUnion(receiverId),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error sending friend request:", error);
    return false;
  }
};

// Function to accept a friend request
const acceptFriendRequest = async (userId: string, friendId: string) => {
  try {
    const userRef = doc(db, "userProfiles", userId);
    const friendRef = doc(db, "userProfiles", friendId);

    // Remove request from both users and add to friends for both users
    await updateDoc(userRef, {
      friendRequests: arrayRemove(friendId),
      friends: arrayUnion(friendId),
      updatedAt: serverTimestamp(),
    });

    await updateDoc(friendRef, {
      friendRequests: arrayRemove(userId),
      friends: arrayUnion(userId),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error accepting friend request:", error);
    return false;
  }
};

// Function to reject a friend request
const rejectFriendRequest = async (userId: string, friendId: string) => {
  try {
    const userRef = doc(db, "userProfiles", userId);
    const friendRef = doc(db, "userProfiles", friendId);

    // Remove request from both users
    await updateDoc(userRef, {
      friendRequests: arrayRemove(friendId),
      updatedAt: serverTimestamp(),
    });

    await updateDoc(friendRef, {
      friendRequests: arrayRemove(userId),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error rejecting friend request:", error);
    return false;
  }
};

// Function to remove friend
const removeFriend = async (userId: string, friendId: string) => {
  try {
    const userRef = doc(db, "userProfiles", userId);
    const friendRef = doc(db, "userProfiles", friendId);

    // Remove from friends list for both users
    await updateDoc(userRef, {
      friends: arrayRemove(friendId),
      updatedAt: serverTimestamp(),
    });

    await updateDoc(friendRef, {
      friends: arrayRemove(userId),
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error removing friend:", error);
    return false;
  }
};

// Function to upload a video or image to Firebase Storage and return its URL
const uploadMedia = async (file: File, folder: string): Promise<string> => {
  try {
    const storage = getStorage();
    const fileName = `${Date.now()}_${file.name}`;
    const fileRef = ref(storage, `${folder}/${fileName}`);

    await uploadBytes(fileRef, file, {
      contentType: file.type,
    });

    const url = await getDownloadURL(fileRef);
    console.log("Media uploaded successfully:", url);
    return url;
  } catch (error) {
    console.error("Error uploading media:", error);
    throw error;
  }
};

export {
  createPost,
  getPosts,
  createComment,
  getCommentsByIds,
  likePost,
  unlikePost,
  sendFriendRequest,
  acceptFriendRequest,
  rejectFriendRequest,
  removeFriend,
  uploadMedia,
  type UserProfile,
  type Post,
  type Comment,
};
